import _ from 'lodash';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { frequencyOptions } from 'constants/options';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyTaxesInfo } from '../PropertyTaxes.constants';

export const configFields = {
  type: {
    fieldId: 'impoundTypeId',
    type: FieldType.SELECT,
    nameInModel: `impoundTypeId`,
    inputProps: {
      options: [],
      placeholder: 'Property Tax Type',
      label: 'Property Tax Type',
      required: true,
      variant: 'outlined',
    },
  },
  frequency: {
    fieldId: 'frequency',
    type: FieldType.SELECT,
    nameInModel: `frequency`,
    inputProps: {
      options: frequencyOptions,
      withSortedOptions: false,
      placeholder: 'Frequency',
      label: 'Frequency',
      required: false,
      variant: 'outlined',
    },
  },
  paidThroughDate: {
    fieldId: 'paidThroughDate',
    type: FieldType.DATE,
    nameInModel: `paidThroughDate`,
    inputProps: {
      format: COMMON_DATE_FORMAT,
      label: 'Paid Through Date',
      required: false,
      variant: 'outlined',
    },
  },
  completionDate: {
    fieldId: 'completionDate',
    type: FieldType.DATE,
    nameInModel: 'completionDate',
    inputProps: {
      format: COMMON_DATE_FORMAT,
      label: 'Completion Date',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  lastPaymentAmount: {
    fieldId: 'lastPaymentAmount',
    type: FieldType.INPUT,
    nameInModel: `lastPaymentAmount`,
    inputProps: {
      label: 'Last Payment Amount',
      placeholder: 'Last Payment Amount',
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      minValue: 0,
      required: false,
    },
  },
  qualifyingForMonthlyAmount: {
    fieldId: 'qualifyingForMonthlyAmount',
    type: FieldType.CHECKBOX,
    nameInModel: `qualifyingForMonthlyAmount`,
    inputProps: {
      widthInColumns: 2,
      label: 'Qualifying for Monthly Amount',
      disabled: false,
      type: FieldInputType.BOOL,
    },
  },
  monthlyAmount: {
    fieldId: 'monthlyAmount',
    type: FieldType.INPUT,
    nameInModel: 'monthlyAmount',
    inputProps: {
      placeholder: 'Monthly Amount',
      label: 'Monthly Amount',
      disabled: false,
      required: true,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
    },
  },
  annualAmount: {
    fieldId: 'annualAmount',
    type: FieldType.INPUT,
    nameInModel: 'annualAmount',
    noValidate: true,
    inputProps: {
      placeholder: 'Annual Amount',
      label: 'Annual Amount',
      disabled: true,
      required: true,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
    },
  },
  amountDueDates: {
    fieldId: 'amountDueDates',
    type: FieldType.INPUT,
    nameInModel: `amountDueDates`,
    inputProps: {
      customStyles: { visibility: 'hidden' },
    },
  },
  dueDate: {
    fieldId: 'dueDate',
    type: FieldType.DATE,
    nameInModel: 'amountDueDates[0].dueDate',
    inputProps: {
      format: COMMON_DATE_FORMAT,
      label: 'Due Date',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  amountDue: {
    fieldId: 'amountDue',
    type: FieldType.INPUT,
    nameInModel: 'amountDueDates[0].amountDue',
    inputProps: {
      placeholder: 'Type Amount Due...',
      label: 'Amount Due',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      minValue: 0,
    },
  },
};

export const getPropertyTaxesConfig = compose(
  fillConfigFieldsWithKeys,
  ({ propertyTaxTypeOptions = [], frequency, onFrequencyChange }) => {
    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...configFields.type,
              inputProps: {
                ...configFields.type.inputProps,
                options: propertyTaxTypeOptions,
              },
            },
            configFields.annualAmount,
            configFields.monthlyAmount,
          ],
          key: `${PropertyTaxesInfo.KEY}-1`,
        },
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...configFields.frequency,
              inputProps: {
                ...configFields.frequency.inputProps,
                customOnChange: onFrequencyChange,
              },
            },
            configFields.paidThroughDate,
            configFields.lastPaymentAmount,
            configFields.amountDueDates,
          ],
          key: `${PropertyTaxesInfo.KEY}-2`,
        },
        ..._.range(frequency).map((__, index) => ({
          type: FieldType.GROUP,
          key: `${PropertyTaxesInfo.KEY}-due-dates-${index}`,
          fields: [
            {
              ...configFields.dueDate,
              nameInModel: configFields.dueDate.nameInModel.replace('[0]', `[${index}]`),
              noValidate: true,
              inputProps: {
                ...configFields.dueDate.inputProps,
                label: `${configFields.dueDate.inputProps.label} ${index + 1}`,
              },
            },
            {
              ...configFields.amountDue,
              nameInModel: configFields.amountDue.nameInModel.replace('[0]', `[${index}]`),
              noValidate: true,
              inputProps: {
                ...configFields.amountDue.inputProps,
                label: `${configFields.amountDue.inputProps.label} ${index + 1}`,
              },
            },
          ],
        })),
      ],
    };
  },
);
